import { FC, Fragment, useEffect, useState } from "react";
import { Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ControlledForm from "Components/ControlledForm";
import { SideModalProps } from "DataTypes/Modal";
import Category from "./Category";
import Time from "./Time";
import { CheckStringEmpty } from "Utils/common";
import TabPanel from "Components/TabPanel";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";

import { setAlertData } from "Redux/Screen/Action";
import AddModal from "./AddModal";
import { useUploadForm } from "Hooks/useUploadFile";
import RedoxFormatter from "Utils/Redox.Formatter";
import Format from "./Format";

import { CategoryArr } from "./Data";

import RedoxApi from "Service/Redox.api";
import PatientApi from "Service/Patient.api";

import { PatientInitialStateType } from "DataTypes/Redux";
import { objectValues } from "DataTypes/Services/Index.type";
import { RedoxCategoryType } from "DataTypes/PatientManagement.type";
import ViewModal from "./ViewModal";
import { CatSearchDocType } from "DataTypes/Services/Redox.type";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import { EhrDomains } from "Constant/Ehr";
import CstmBtn from "Components/CstmBtn";
import { label } from "Constant/En";
import EhrApi from "Service/Ehr.api";

const Find: FC<SideModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const { patientInfo, patientOrg, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const crudPermission = ehr.vendor === null;
  const ehrSync = useEhrSync(EhrDomains.DOCUMENTS);
  // Indexing reads the documents a sync is writing, so the two racing produced the
  // fluctuating count (CV-unreported): re-indexing mid-sync could pick up a partial
  // write. ehrSync.syncing only catches a documents-scoped sync; a broader "Sync All"
  // sets activeSyncDomain to EhrDomains.ALL instead, so that is checked too.
  const syncBlockingIndex =
    ehrSync.syncing || ehr?.activeSyncDomain === EhrDomains.ALL;
  // Arrow, not a bare reference: handleCatApiCall is a const declared further
  // down, so naming it here would hit the temporal dead zone.
  useEhrRefresh(EhrDomains.DOCUMENTS, () => handleCatApiCall());
  const [openModal, setOpenModal] = useState(false);
  const [tabValue, setTabValue] = useState(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [indexLoading, setIndexLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [categoryType, setCategoryType] = useState<string | null>(null);
  const [categoryArr, setCategoryArr] = useState<RedoxCategoryType[]>(
    structuredClone(CategoryArr),
  );
  const [timeTabData, setTimeTabData] = useState<objectValues[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [viewDoc, setViewDoc] = useState<CatSearchDocType | null>(null);
  const { uploadForm, progress, setProgress, inProgress } = useUploadForm(
    `${process.env.REACT_APP_BASE_URL}/redox` +
      `/patient/${patientInfo.id}/document/${categoryType}/upload`,
  );
  const {
    uploadForm: uploadToEcw,
    progress: ecwProgress,
    setProgress: setEcwProgress,
    inProgress: ecwInProgress,
  } = useUploadForm(
    `${process.env.REACT_APP_BASE_EHR_URL}/ehr-ecw` +
      `/practice-location/${patientOrg?.practiceLocationId}` +
      `/api/write/document-reference/upload?patientId=${encodeURIComponent(
        patientInfo.id || "",
      )}` +
      `&title=${encodeURIComponent(selectedFile?.name || "")}` +
      `&folder=${encodeURIComponent(categoryType || "Misc")}`,
  );

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  };

  useEffect(() => {
    if (searchValue == "") {
      tabValue == 1 ? handleCatApiCall() : handleTimeApiCall();
    }
    setSearchValue("");
  }, [tabValue]);

  useEffect(() => {
    setSelectedFile(null);
  }, [categoryType]);

  useEffect(() => {
    setProgress(0);
  }, [selectedFile]);

  useEffect(() => {
    const timer = setTimeout(() => {
      tabValue == 1 ? handleCatApiCall() : handleTimeApiCall();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // CategorySearch/timeSeries are dedupe: true, so commonFetch cancels a
  // stale in-flight request itself when a newer one starts; a canceled
  // response comes back as { success: false, canceled: true } and is
  // dropped here instead of being treated as a real failure.
  //
  // One request per category. Solr sorts results by documentType, so a single page of 100 was
  // filled entirely by whichever category sorts first: a patient with 287 clinical documents got
  // none of their 16 lab results, while the facet count still said 16. Each category now asks
  // for its own documents. Each also gets its own dedupeKey -- sharing the subUrl key would make
  // the four abort one another, whereas a per-category key still lets a newer keystroke cancel
  // that category's stale request.
  const handleCatApiCall: () => void = async () => {
    setLoading(true);
    const results = await Promise.all(
      categoryArr.map((cat) =>
        RedoxApi.CategorySearch({
          data: {
            keyword: searchValue,
            limit: 100,
            offset: 0,
            documentType: cat.docKey,
          },
          id1: patientInfo.id,
          dedupeKey: `category-search:${patientInfo.id}:${cat.docKey}`,
        })
      )
    );
    if (results.some((res) => res?.canceled)) return;
    // A category that fails comes back empty rather than blanking the ones that loaded.
    setCategoryArr(
      categoryArr.map((cat, index) => {
        const res = results[index];
        if (!res?.success) {
          return structuredClone(CategoryArr.find((el) => el.id === cat.id) || cat);
        }
        const CheckRes = RedoxFormatter.CategorySearch(res?.data);
        return Format.FormatCatSearch(CheckRes, [cat])[0];
      })
    );
    setLoading(false);
  };

  const handleTimeApiCall = async (): Promise<void> => {
    setLoading(true);
    const payload = { keyword: searchValue };
    const res: any = await PatientApi.timeSeries({
      data: payload,
      id1: patientInfo.id,
      offset: 0,
    });
    if (res?.canceled) return;
    if (res?.success) setTimeTabData(res?.data);
    setLoading(false);
  };

  const handleDocIndexing: () => void = async () => {
    if (indexLoading || syncBlockingIndex) return;
    setIndexLoading(true);
    try {
      const res = await RedoxApi.DocIndex({ id1: patientInfo.id });
      if (res?.success) {
        dispatch(setAlertData(res?.alert));
        tabValue == 1 && handleCatApiCall();
      }
    } finally {
      setIndexLoading(false);
    }
  };

  const hanldeImportFile: () => void = async () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await uploadForm({
        data: formData,
      });
      if (res?.success) {
        res?.alert && dispatch(setAlertData(res.alert));
        tabValue == 1 && handleCatApiCall();
      }
    }
  };

  const handleEcwUpload: () => void = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("file", selectedFile);
    const res = await uploadToEcw({ data: formData });
    if (res?.success) {
      // eCW can expose a filed PDF under a different id than the write response.
      // Sync now so the local row receives the readable id before it is displayed.
      const syncRes: any = await EhrApi.syncEHRDomains({
        id1: patientInfo.id || "",
        data: { domain: EhrDomains.DOCUMENTS },
      });
      await handleCatApiCall();
      dispatch(
        setAlertData({
          alertMessage: syncRes?.success
            ? "Document uploaded to eCW and refreshed."
            : "Document uploaded to eCW; use Sync to refresh its readable ID.",
          openAlert: true,
          alertVariant: syncRes?.success ? "success" : "warning",
        }),
      );
      handleCloseModal();
    }
  };

  useEffect(() => {
    setModalType(viewDoc ? "view" : "");
  }, [viewDoc]);

  useEffect(() => {
    setOpenModal(modalType ? true : false);
  }, [modalType]);

  const handleCloseModal: () => void = () => {
    setEcwProgress(0);
    setModalType("");
    setViewDoc(null);
    setSelectedFile(null);
    setCategoryType(null);
  };

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Find</Typography>
        {/* Controls on one line, with the sync stamp beneath them:
            stacking it under the button alone makes that column taller
            and drops the icons out of line with it. */}
        <Box
          sx={{
            ...CommonStyle.sxFlexDirectionColumn,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ ...CommonStyle.sxRow }}>
            {/* The documents domain is the patient's, not the tab's: the
                sync stays offered on either tab so it is not hidden behind
                the one the user did not open. */}
            {ehrSync.canSync && (
              <Tooltip arrow placement="top"
                title={
                  indexLoading
                    ? "Sync is disabled while indexing is in progress"
                    : ehrSync.title
                }
              >
                <span>
                  <CstmBtn
                    label={label.syncFromEhr}
                    btnType="primary"
                    loading={ehrSync.syncing}
                    loadingText="Syncing..."
                    disable={indexLoading}
                    onClick={ehrSync.handleSync}
                    sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                  />
                </span>
              </Tooltip>
            )}
            {tabValue == 1 && (
              <Fragment>
                {crudPermission && (
                  <Box
                    sx={{ ...CommonStyle.sxRoundIconsMainSm }}
                    onClick={() => setModalType("upload")}
                  >
                    <ImgPath.ImportIcon titleAccess="Document Upload" />
                  </Box>
                )}
                <Box
                  sx={{
                    ...(indexLoading || syncBlockingIndex
                      ? CommonStyle.sxRoundIconsDisabledSm
                      : CommonStyle.sxRoundIconsMainSm),
                    mx: 1,
                  }}
                  {...(!indexLoading &&
                    !syncBlockingIndex && { onClick: handleDocIndexing })}
                >
                  <ImgPath.RefreshIcon
                    titleAccess={
                      syncBlockingIndex
                        ? "Indexing is disabled while a sync is in progress"
                        : indexLoading
                          ? "Indexing..."
                          : "Indexing"
                    }
                  />
                </Box>
              </Fragment>
            )}

            <Box sx={{ ...CommonStyle.sxRoundIconsMainSm }} onClick={onClose}>
              <ImgPath.CloseIcon titleAccess="Close" />
            </Box>
          </Box>
          <EhrLastSync sync={ehrSync} />
        </Box>
      </Box>
      <Box sx={{ flexShrink: 0, px: 2, py: 0.5 }}>
        <ControlledForm.Input
          id="search"
          placeholder="Search"
          iconId="search"
          onChange={(val) => setSearchValue(CheckStringEmpty(val))}
          value={searchValue}
        />
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Time" {...a11yProps(0)} />
          <Tab label="Category" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        <Time data={timeTabData} loading={loading} />
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        <Category
          data={categoryArr}
          loading={loading}
          setViewDoc={(data) => setViewDoc(data)}
        />
      </TabPanel>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "28%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%" />
      {openModal && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {crudPermission && modalType == "upload" && (
            <AddModal
              changeFile={(data) => setSelectedFile(data)}
              fileName={selectedFile?.name || ""}
              progress={progress}
              inProgress={inProgress}
              handleClose={handleCloseModal}
              handleUpload={hanldeImportFile}
              changeCategory={(val) => setCategoryType(val)}
              categoryVal={categoryType}
              acceptedFileTypes=".pdf,application/pdf"
            />
          )}
          {modalType == "ecw-upload" && (
            <AddModal
              title="Upload Document to EHR"
              changeFile={(data) => setSelectedFile(data)}
              fileName={selectedFile?.name || ""}
              progress={ecwProgress}
              inProgress={ecwInProgress}
              handleClose={handleCloseModal}
              handleUpload={handleEcwUpload}
              changeCategory={(val) => setCategoryType(val)}
              categoryVal={categoryType}
            />
          )}
          {modalType == "view" && viewDoc && (
            <ViewModal file={viewDoc} handleClose={handleCloseModal} />
          )}
        </Box>
      )}
    </Fragment>
  );
};

export default Find;

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxTab = {
  flexShrink: 0,
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

const sxTabPanel = {
  flex: 1,
  minHeight: 0,
};
